import React, { useCallback, useMemo } from 'react'
import styles from './VarietyOptions.module.css'
import { Cell, Column, CompactTable, Row, Table, TableBody, TableHeader } from '@/components/Table'
import { Button } from '@/components/Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt, faUndo } from '@fortawesome/free-solid-svg-icons'
import { SearchIngredient, VarietyLevel, VarietyOptions } from '@/api/types'
import { Radio, RadioGroup } from '@/components/RadioGroup'
import { Label } from '@/components/Label'
import { CollapsibleSection } from '@/components/CollapsibleSection/CollapsibleSection'

type VarietyOptionsProps = {
  ingredients: Array<SearchIngredient>
  varietyOptions: VarietyOptions
  setVarietyOptions: (options: VarietyOptions) => void
  action: 'CREATE' | 'RECREATE' | 'REVIEW'
}

export function VarietyOptionsUI({
  ingredients,
  varietyOptions,
  setVarietyOptions,
  action,
}: VarietyOptionsProps) {
  const isRevision = action === 'REVIEW'

  const priorityIngredientsSet = useMemo(() => {
    return new Set(varietyOptions.priorityIngredients)
  }, [varietyOptions.priorityIngredients])

  const ingredientById = useMemo(() => {
    const map: Record<number, SearchIngredient> = {}
    ingredients.forEach((ing) => {
      map[ing.idIngredient] = ing
    })
    return map
  }, [ingredients])

  const restrictedIngredientsData = useMemo(() => {
    return varietyOptions.excludedIngredients
      .map((id) => ingredientById[id])
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [varietyOptions, ingredientById])

  const ingredientsData = useMemo(() => {
    const ingredients = isRevision
      ? varietyOptions.allowedIngredients
      : varietyOptions.priorityIngredients
    return ingredients.map((id) => ingredientById[id]).sort((a, b) => a.name.localeCompare(b.name))
  }, [varietyOptions, ingredientById])

  const handleAddIngredient = useCallback(
    (ingredient: number) => {
      if (!isRevision) {
        setVarietyOptions({
          ...varietyOptions,
          priorityIngredients: [...varietyOptions.priorityIngredients, ingredient],
        })
      } else {
        const excludedIngredients = varietyOptions.excludedIngredients.filter(
          (id) => id !== ingredient,
        )
        setVarietyOptions({
          ...varietyOptions,
          excludedIngredients,
          allowedIngredients: [...varietyOptions.allowedIngredients, ingredient],
        })
      }
    },
    [isRevision, varietyOptions],
  )

  const handleRestrictIngredient = useCallback(
    (ingredient: number) => {
      setVarietyOptions({
        ...varietyOptions,
        excludedIngredients: isRevision
          ? [...varietyOptions.excludedIngredients, ingredient]
          : varietyOptions.excludedIngredients,
        allowedIngredients: varietyOptions.allowedIngredients.filter((id) => id !== ingredient),
      })
    },
    [varietyOptions],
  )

  return (
    <div className={styles.container}>
      <Label>Variety Level</Label>
      <RadioGroup
        variation="segmented"
        value={varietyOptions.varietyLevel}
        onChange={(value: string) =>
          setVarietyOptions({
            ...varietyOptions,
            varietyLevel: value as VarietyLevel,
          })
        }>
        <Radio key="LOW" value="LOW">
          Low
        </Radio>
        <Radio key="UNLIMITED" value="UNLIMITED">
          Balanced
        </Radio>
      </RadioGroup>
      <CollapsibleSection label={`Ingredients (${ingredientsData.length})`}>
        <div className={styles.section}>
          <div>
            <Table aria-label="Ingredients" key={Math.random()}>
              <TableHeader>
                <Column id="name" isRowHeader>
                  Name
                </Column>
                <Column id="englishName" isRowHeader>
                  English Name
                </Column>
                <Column id="actions" width={80}>
                  Restrict
                </Column>
              </TableHeader>
              <TableBody items={ingredientsData}>
                {(ingredient) => {
                  const isPriority = priorityIngredientsSet.has(ingredient.idIngredient)
                  return (
                    <Row
                      id={ingredient.idIngredient}
                      className={
                        isRevision && isPriority ? styles.priorityIngredientRow : undefined
                      }>
                      <Cell>{ingredient.name}</Cell>
                      <Cell>{ingredient.englishName}</Cell>
                      <Cell>
                        <Button
                          variant="icon"
                          aria-label={`Restrict ${ingredient.name}`}
                          onPress={() => {
                            handleRestrictIngredient(ingredient.idIngredient)
                          }}>
                          <FontAwesomeIcon icon={faTrashAlt} color="var(--invalid-color)" />
                        </Button>
                      </Cell>
                    </Row>
                  )
                }}
              </TableBody>
            </Table>
          </div>
        </div>
      </CollapsibleSection>
      <CollapsibleSection
        label={`Restricted Ingredients (${restrictedIngredientsData.length})`}
        disabled={!isRevision}>
        <div>
          <Table aria-label="Restricted Ingredients" key={Math.random()}>
            <TableHeader>
              <Column id="name" isRowHeader>
                Name
              </Column>
              <Column id="englishName" isRowHeader>
                English Name
              </Column>
              <Column id="actions" width={80}>
                Allow
              </Column>
            </TableHeader>
            <TableBody
              items={varietyOptions.excludedIngredients
                .map((id) => ingredientById[id])
                .sort((a, b) => a.name.localeCompare(b.name))}>
              {(ingredient) => (
                <Row id={ingredient.idIngredient} key={ingredient.idIngredient}>
                  <Cell>{ingredient.name}</Cell>
                  <Cell>{ingredient.englishName}</Cell>
                  <Cell>
                    <Button
                      variant="icon"
                      aria-label={`Allow ${ingredient.name}`}
                      onPress={() => {
                        handleAddIngredient(ingredient.idIngredient)
                      }}>
                      <FontAwesomeIcon icon={faUndo} color="var(--primary-color)" />
                    </Button>
                  </Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </div>
      </CollapsibleSection>
    </div>
  )
}
